/**
 * Client-side API cache.
 *
 * Goal: a list is downloaded once per session and every later visit to that
 * screen is instant - but the moment anything is added, updated or deleted
 * (by this user or by anyone else) the affected screens refetch immediately.
 *
 * How it works
 *  - Installed as an axios adapter, so all existing `axios.get(...)` calls are
 *    covered without touching a single service file.
 *  - GET responses for the endpoints listed in CACHE_RULES are kept in memory
 *    (and, for slow-moving master data, in sessionStorage so a browser refresh
 *    stays fast too).
 *  - Identical concurrent GETs share one network request. This alone removes a
 *    lot of duplicate traffic, because several components often ask for the
 *    same list while a page mounts.
 *  - Any successful POST/PUT/PATCH/DELETE drops the cache of the entity it
 *    touched plus everything derived from it, and notifies subscribers so open
 *    screens refetch.
 *  - The server broadcasts `data:invalidated` over the websocket, which does
 *    the same thing for changes made by other users. Events caused by this tab
 *    are ignored (we already handled them locally) so nothing is fetched twice.
 *  - If the websocket is down we cannot hear about other people's changes, so
 *    the cache degrades to a short 20s window: still enough to dedupe a page's
 *    own burst of requests, never long enough to show meaningfully stale data.
 */

const MINUTE = 60 * 1000;

/** Mirror of CAE-BACKEND/config/cacheRules.js - keep both files in sync. */
const CACHE_RULES = {
  university: {
    ttl: 10 * MINUTE,
    persist: true,
    endpoints: [
      "get-all-university",
      "get-all-university-without-token",
      "get-univesity-by-organisaction/",
      "getAvailableDegreesByUniversity/",
      "getAvailableProgramsByUniversityAndDegree/",
      "get-get-university-by-id/",
      "get-university-details-with-programs-catalog-processsheets/",
    ],
  },
  organization: {
    ttl: 5 * MINUTE,
    endpoints: [
      "get-all-organizations",
      "get-all-organizations-names",
      "get-organization-by-id/",
      "get-organisaction-activity-by-organisaction/",
    ],
  },
  program: {
    ttl: 10 * MINUTE,
    persist: true,
    endpoints: ["get-all-programs", "get-program-by-id/"],
  },
  degree: {
    ttl: 30 * MINUTE,
    persist: true,
    endpoints: ["get-all-degrees", "get-degree-by-id/"],
  },
  currency: { ttl: 30 * MINUTE, persist: true, endpoints: ["get-all-currencies"] },
  "country-state": {
    ttl: 30 * MINUTE,
    persist: true,
    endpoints: ["countries", "get-states-by-country", "get-all-counties-with-states"],
  },
  "contract-type": { ttl: 30 * MINUTE, persist: true, endpoints: ["get-all-contract-types"] },
  role: { ttl: 30 * MINUTE, persist: true, endpoints: ["get-all-roles"] },
  branch: {
    ttl: 10 * MINUTE,
    endpoints: ["get-all-braches", "get-branches-by-organisation/", "get-branch-by-id/"],
  },
  catalog: {
    ttl: 10 * MINUTE,
    endpoints: [
      "get-all-catalogs-or-brochures",
      "get-catalogs-by-id/",
      "get-catalogs-by-universityId/",
    ],
  },
  "process-sheet": {
    ttl: 10 * MINUTE,
    endpoints: [
      "get-all-process-sheets",
      "get-process-sheet-by-id/",
      "get-process-sheet-by-university/",
    ],
  },
  template: {
    ttl: 10 * MINUTE,
    endpoints: ["get-all-email-templates", "get-email-template-by-id/"],
  },
  login: {
    ttl: 2 * MINUTE,
    endpoints: [
      "get-all-users",
      "get-all-active-subAdmins",
      "get-all-active-users",
      "get-all-active-agents",
      "get-user-by-id/",
      "get-user-by-status-and-role/",
      "get-filter-users",
      "settings-data-counter",
    ],
  },
  student: {
    ttl: 1 * MINUTE,
    endpoints: [
      "get-all-students",
      "get-student-by-id/",
      "getStudentStatistics",
      "get-visa-approved-unsettled-student-list",
      "get-disabled-visa-approved-student-list",
      "get-default-intake",
      "get-account-summary",
      "get-dashboard-summary",
    ],
  },
  invoice: {
    ttl: 2 * MINUTE,
    endpoints: [
      "get-all-invoices",
      "get-invoice-by-id/",
      "get-invoice-by-university/",
      "get-invoice-by-organization/",
    ],
  },
  "account-bundle": { ttl: 2 * MINUTE, endpoints: ["light", ""] },
  dashboard: {
    ttl: 1 * MINUTE,
    endpoints: ["get-dashboard-data", "get-university-contacts"],
  },
  support: {
    ttl: 1 * MINUTE,
    endpoints: ["get-all-support-tickets", "get-support-ticket-by-id/"],
  },
};

/** Mirror of CACHE_DEPENDENCIES on the backend. */
const CACHE_DEPENDENCIES = {
  university: ["program", "catalog", "process-sheet", "organization", "student", "dashboard"],
  organization: ["university", "branch", "login", "student", "dashboard"],
  program: ["university", "student", "dashboard"],
  degree: ["university", "program"],
  currency: ["university", "invoice"],
  "contract-type": ["university"],
  branch: ["organization", "login"],
  catalog: ["university"],
  "process-sheet": ["university"],
  role: ["login"],
  login: ["organization", "dashboard"],
  student: ["invoice", "account-bundle", "dashboard", "organization"],
  invoice: ["student", "account-bundle", "dashboard"],
  "account-bundle": ["student", "invoice", "dashboard"],
  template: [],
  "country-state": [],
  dashboard: [],
  support: [],
  notes: ["student"],
  reminder: ["student"],
  request: ["student"],
  // A file move re-points the entity that owns it, which can be any of these.
  folder: ["student", "university", "organization", "catalog", "process-sheet"],
  upload: ["student", "university", "organization", "catalog", "process-sheet"],
};

/** Window in which the cache may still be used while the websocket is down. */
const OFFLINE_TTL = 20 * 1000;
/** Largest payload we are willing to mirror into sessionStorage. */
const MAX_PERSIST_BYTES = 512 * 1024;
/** Subscribers are never woken more than once per entity in this window. */
const NOTIFY_THROTTLE = 300;

const STORAGE_PREFIX = "apiCache:";

/** Identifies this tab so we can ignore the echo of our own writes. */
export const CLIENT_ID =
  Math.random().toString(36).slice(2) + Date.now().toString(36);

/* ------------------------------------------------------------------ *
 * Internals
 * ------------------------------------------------------------------ */

const memory = new Map(); // key -> { data, status, headers, at, entity, ttl }
const inFlight = new Map(); // key -> Promise
const subscribers = new Map(); // entity -> Set<fn>
const lastNotifiedAt = new Map(); // entity -> timestamp
const pendingNotify = new Map(); // entity -> { timer, payload }

const stats = { hits: 0, misses: 0, shared: 0, invalidations: 0 };

// Bumped on every invalidation. A sessionStorage write is deferred to keep it
// off the response path, so it has to check that nothing was invalidated in the
// meantime - otherwise it could resurrect data that was just dropped.
let epoch = 0;

let socketIsUp = () => false;

/** Lets socketClient tell the cache whether realtime updates are flowing. */
export const setRealtimeProbe = (fn) => {
  if (typeof fn === "function") socketIsUp = fn;
};

const pathFromUrl = (url = "") => {
  const withoutOrigin = String(url).replace(/^https?:\/\/[^/]+/i, "");
  const withoutQuery = withoutOrigin.split("?")[0];
  return withoutQuery.replace(/^\/api\/?/, "");
};

/** { entity, ttl, persist } for a cacheable URL, otherwise null. */
const ruleForUrl = (url) => {
  const path = pathFromUrl(url);
  const slash = path.indexOf("/");
  const entity = slash === -1 ? path : path.slice(0, slash);
  const rest = slash === -1 ? "" : path.slice(slash + 1);

  const rule = CACHE_RULES[entity];
  if (!rule) return null;

  const match = rule.endpoints.some((endpoint) =>
    endpoint.endsWith("/") ? rest.startsWith(endpoint) : rest === endpoint
  );
  return match ? { entity, ttl: rule.ttl, persist: Boolean(rule.persist) } : null;
};

export const entityFromUrl = (url) => {
  const path = pathFromUrl(url);
  const slash = path.indexOf("/");
  return slash === -1 ? path : path.slice(0, slash);
};

export const relatedEntities = (entity) => {
  if (!entity) return [];
  const deps = CACHE_DEPENDENCIES[entity];
  return deps ? [entity, ...deps] : [entity];
};

/** Cache key: full url + query, with cache-busting params removed. */
const keyForRequest = (config) => {
  const url = String(config.url || "");
  const [base, query = ""] = url.split("?");
  const params = new URLSearchParams(query);

  if (config.params) {
    Object.entries(config.params).forEach(([name, value]) => {
      if (value !== undefined && value !== null) params.set(name, String(value));
    });
  }
  ["_", "_t", "refresh", "nocache", "noCache"].forEach((name) => params.delete(name));

  const sorted = [...params.entries()].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  const normalized = sorted.map(([name, value]) => `${name}=${value}`).join("&");
  return normalized ? `${base}?${normalized}` : base;
};

const isFresh = (entry) => {
  const ttl = socketIsUp() ? entry.ttl : Math.min(entry.ttl, OFFLINE_TTL);
  return Date.now() - entry.at < ttl;
};

/**
 * Cached payloads are handed out as copies so a caller cannot corrupt the
 * cache. A few endpoints still return tens of MB, and cloning those would cost
 * more than the network call we just saved - so the first clone is timed, and
 * anything expensive is served by reference from then on (that is also what
 * Redux already holds, so nothing is duplicated in memory).
 */
const CLONE_BUDGET_MS = 30;

const copy = (value, entry) => {
  if (entry && entry.noClone) return value;
  try {
    const startedAt = Date.now();
    const cloned =
      typeof structuredClone === "function"
        ? structuredClone(value)
        : JSON.parse(JSON.stringify(value));
    if (entry && Date.now() - startedAt > CLONE_BUDGET_MS) entry.noClone = true;
    return cloned;
  } catch {
    return value;
  }
};

/* ---- sessionStorage mirror (master data only) --------------------- */

const persist = (key, entry) => {
  if (!entry.persist) return;
  const writtenAtEpoch = epoch;
  // Off the critical path: never make a response wait on serialisation.
  setTimeout(() => {
    if (epoch !== writtenAtEpoch) return; // invalidated while we waited
    try {
      const raw = JSON.stringify({ data: entry.data, at: entry.at, entity: entry.entity });
      if (raw.length > MAX_PERSIST_BYTES) return;
      window.sessionStorage.setItem(STORAGE_PREFIX + key, raw);
    } catch {
      /* quota or private mode - the memory cache still works */
    }
  }, 0);
};

const readPersisted = (key, rule) => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const entry = { ...parsed, ttl: rule.ttl, persist: true, status: 200, headers: {} };
    if (!isFresh(entry)) {
      window.sessionStorage.removeItem(STORAGE_PREFIX + key);
      return null;
    }
    memory.set(key, entry);
    return entry;
  } catch {
    return null;
  }
};

const dropPersisted = (entities) => {
  try {
    const doomed = [];
    for (let i = 0; i < window.sessionStorage.length; i += 1) {
      const key = window.sessionStorage.key(i);
      if (!key || !key.startsWith(STORAGE_PREFIX)) continue;
      const entity = entityFromUrl(key.slice(STORAGE_PREFIX.length));
      if (entities.has(entity)) doomed.push(key);
    }
    doomed.forEach((key) => window.sessionStorage.removeItem(key));
  } catch {
    /* ignore */
  }
};

/* ------------------------------------------------------------------ *
 * Invalidation + subscriptions
 * ------------------------------------------------------------------ */

/** Drops cached data for the given entities. Does not notify anyone. */
export const clearApiCache = (entities) => {
  const list = entities === undefined || entities === "all" ? null : [].concat(entities);
  stats.invalidations += 1;
  epoch += 1;

  if (!list) {
    memory.clear();
    try {
      const doomed = [];
      for (let i = 0; i < window.sessionStorage.length; i += 1) {
        const key = window.sessionStorage.key(i);
        if (key && key.startsWith(STORAGE_PREFIX)) doomed.push(key);
      }
      doomed.forEach((key) => window.sessionStorage.removeItem(key));
    } catch {
      /* ignore */
    }
    return;
  }

  const targets = new Set(list);
  [...memory.entries()].forEach(([key, entry]) => {
    if (targets.has(entry.entity)) memory.delete(key);
  });
  dropPersisted(targets);
};

/**
 * Subscribe to "this entity changed, refetch it".
 * @returns {Function} unsubscribe
 */
export const subscribeToInvalidation = (entity, handler) => {
  if (typeof handler !== "function") return () => {};
  const key = entity || "all";
  if (!subscribers.has(key)) subscribers.set(key, new Set());
  subscribers.get(key).add(handler);
  return () => {
    const listeners = subscribers.get(key);
    if (listeners) listeners.delete(handler);
  };
};

const fire = (entity, payload) => {
  const listeners = subscribers.get(entity);
  if (!listeners || !listeners.size) return;
  lastNotifiedAt.set(entity, Date.now());
  listeners.forEach((handler) => {
    try {
      handler(payload);
    } catch (error) {
      console.warn("[apiCache] subscriber failed:", error);
    }
  });
};

const notify = (entities, payload) => {
  const targets = new Set([...entities, "all"]);

  targets.forEach((entity) => {
    const listeners = subscribers.get(entity);
    if (!listeners || !listeners.size) return;

    // Several related entities usually change at once (a university edit also
    // touches programs, catalogs, ...), so wake each screen at most once per
    // window. Bursts are coalesced into a trailing call rather than dropped -
    // an update must never be silently lost.
    const since = Date.now() - (lastNotifiedAt.get(entity) || 0);
    if (since >= NOTIFY_THROTTLE) {
      const queued = pendingNotify.get(entity);
      if (queued) {
        clearTimeout(queued.timer);
        pendingNotify.delete(entity);
      }
      fire(entity, payload);
      return;
    }

    const queued = pendingNotify.get(entity);
    if (queued) {
      queued.payload = payload; // keep the newest reason
      return;
    }
    const timer = setTimeout(() => {
      const item = pendingNotify.get(entity);
      pendingNotify.delete(entity);
      if (item) fire(entity, item.payload);
    }, NOTIFY_THROTTLE - since);
    pendingNotify.set(entity, { timer, payload });
  });
};

/** A write made by this tab: clear locally and refresh what is on screen. */
export const invalidateLocally = (entity) => {
  const entities = relatedEntities(entity);
  if (!entities.length) return;
  clearApiCache(entities);
  notify(entities, { entity, entities, origin: "local", timestamp: Date.now() });
};

/** A `data:invalidated` websocket event from the server. */
export const handleRemoteInvalidation = (payload = {}) => {
  // Our own write already cleared everything synchronously.
  if (payload.origin && payload.origin === CLIENT_ID) return;

  if (payload.entity === "all") {
    clearApiCache();
    notify(["all"], { ...payload, origin: "remote" });
    return;
  }

  const entities =
    Array.isArray(payload.entities) && payload.entities.length
      ? payload.entities
      : relatedEntities(payload.entity);

  if (!entities.length) return;
  clearApiCache(entities);
  notify(entities, { ...payload, entities, origin: "remote" });
};

/* ------------------------------------------------------------------ *
 * axios adapter
 * ------------------------------------------------------------------ */

const isWriteMethod = (method) =>
  ["post", "put", "patch", "delete"].includes(String(method || "get").toLowerCase());

/**
 * Installs the cache on an axios instance (or the default export).
 * Safe to call once, at app start-up.
 */
export const installApiCache = (axios) => {
  if (!axios || axios.__apiCacheInstalled) return;

  const baseAdapter = axios.getAdapter(axios.defaults.adapter);

  axios.defaults.adapter = async (config) => {
    const method = String(config.method || "get").toLowerCase();

    // Tag every request so the server can tell us apart from other tabs.
    if (config.headers && typeof config.headers.set === "function") {
      config.headers.set("X-Client-Id", CLIENT_ID);
    } else {
      config.headers = { ...(config.headers || {}), "X-Client-Id": CLIENT_ID };
    }

    if (isWriteMethod(method)) {
      const response = await baseAdapter(config);
      if (response.status >= 200 && response.status < 300) {
        invalidateLocally(entityFromUrl(config.url));
      }
      return response;
    }

    if (
      method !== "get" ||
      config.responseType === "blob" ||
      config.responseType === "arraybuffer"
    ) {
      return baseAdapter(config);
    }

    const rule = ruleForUrl(config.url);
    if (!rule) return baseAdapter(config);

    const key = keyForRequest(config);
    const bust = /[?&](_|_t|refresh|nocache|noCache)=/.test(String(config.url || ""));

    if (!bust) {
      let entry = memory.get(key);
      if (!entry && rule.persist) entry = readPersisted(key, rule);

      if (entry && isFresh(entry)) {
        stats.hits += 1;
        return {
          data: copy(entry.data, entry),
          status: entry.status || 200,
          statusText: "OK (cache)",
          headers: { ...(entry.headers || {}), "x-client-cache": "HIT" },
          config,
          request: null,
        };
      }
      if (entry) memory.delete(key);

      // Same request already on the wire: ride along instead of duplicating it.
      const pending = inFlight.get(key);
      if (pending) {
        stats.shared += 1;
        const response = await pending;
        return { ...response, data: copy(response.data), config };
      }
    }

    stats.misses += 1;
    const request = baseAdapter(config)
      .then((response) => {
        if (response.status >= 200 && response.status < 300) {
          const entry = {
            data: response.data,
            status: response.status,
            headers: {
              etag: response.headers && response.headers.etag,
              "x-cache": response.headers && response.headers["x-cache"],
            },
            at: Date.now(),
            entity: rule.entity,
            ttl: rule.ttl,
            persist: rule.persist,
          };
          memory.set(key, entry);
          persist(key, entry);
        }
        return response;
      })
      .finally(() => {
        inFlight.delete(key);
      });

    inFlight.set(key, request);
    return request;
  };

  axios.__apiCacheInstalled = true;

  // Handy in the console: window.__apiCache.stats()
  if (typeof window !== "undefined") {
    window.__apiCache = {
      stats: () => ({ ...stats, entries: memory.size }),
      clear: clearApiCache,
      keys: () => [...memory.keys()],
    };
  }
};

const apiCache = {
  CLIENT_ID,
  clearApiCache,
  entityFromUrl,
  handleRemoteInvalidation,
  installApiCache,
  invalidateLocally,
  relatedEntities,
  setRealtimeProbe,
  subscribeToInvalidation,
};

export default apiCache;
